import React, { useState, useEffect, useMemo } from 'react';
import Icon from '../shared/Icon';
import { apiService } from '../../../services/api';
import './ReviewMediaPanel.css';

/**
 * ReviewMediaPanel (PRD Mobile Field Capture, R5) — lets a reviewer see a captured
 * image beside its extracted fields. Left: image file list. Right: the selected
 * file's image (streamed through resapi as an object URL) next to its fields.
 */
const IMAGE_EXT = ['.png', '.jpg', '.jpeg', '.webp', '.gif'];
const isImageName = (name) => IMAGE_EXT.some((e) => (name || '').toLowerCase().endsWith(e));

// Keys carried on extraction rows for plumbing, not values a reviewer should see.
const INTERNAL_KEYS = new Set(['id', 'org_id', 'file_id', 'dataset_id', '_metadata', 'evidence']);

function ReviewMediaPanel({ datasetId, files = [], resultsByFile = {} }) {
  const imageFiles = useMemo(() => files.filter((f) => isImageName(f.file_name)), [files]);
  const [selectedId, setSelectedId] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageState, setImageState] = useState('idle'); // idle | loading | ready | error

  // Default selection: the first image capture.
  useEffect(() => {
    if (!selectedId && imageFiles.length > 0) setSelectedId(imageFiles[0].file_id);
  }, [imageFiles, selectedId]);

  // Load the selected file's image; revoke the previous object URL on change/unmount.
  useEffect(() => {
    if (!selectedId) return undefined;
    let cancelled = false;
    let created = null;
    setImageState('loading');
    setImageUrl(null);
    apiService.fetchFileImageObjectUrl(datasetId, selectedId).then((objUrl) => {
      if (cancelled) {
        if (objUrl) URL.revokeObjectURL(objUrl);
        return;
      }
      if (objUrl) {
        created = objUrl;
        setImageUrl(objUrl);
        setImageState('ready');
      } else {
        setImageState('error');
      }
    });
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [datasetId, selectedId]);

  const fields = resultsByFile[selectedId] || null;
  const fieldEntries = fields ? Object.entries(fields).filter(([k]) => !INTERNAL_KEYS.has(k)) : [];

  return (
    <div className="review-media">
      <ul className="review-media-filelist">
        {imageFiles.map((f) => (
          <li key={f.file_id}>
            <button
              type="button"
              className={`review-media-fileitem ${f.file_id === selectedId ? 'review-media-fileitem--active' : ''}`}
              onClick={() => setSelectedId(f.file_id)}
              title={f.file_name}
            >
              <Icon name="image" className="review-media-fileicon" size={18} />
              <span className="review-media-filename">{f.file_name}</span>
            </button>
          </li>
        ))}
      </ul>

      <div className="review-media-content">
        <div className="review-media-imagepane">
          {imageState === 'loading' && <p className="review-media-hint">Loading image…</p>}
          {imageState === 'error' && <p className="review-media-hint">Preview unavailable for this capture.</p>}
          {imageState === 'ready' && imageUrl && (
            <img src={imageUrl} alt="Captured document" className="review-media-image" />
          )}
        </div>

        <div className="review-media-fields">
          <h4 className="review-media-fields-title">Extracted fields</h4>
          {fieldEntries.length === 0 ? (
            <p className="review-media-hint">No extracted fields for this capture yet.</p>
          ) : (
            <dl className="review-media-fieldlist">
              {fieldEntries.map(([k, v]) => (
                <div key={k} className="review-media-field">
                  <dt className="review-media-field-label">{k === 'answer' ? 'AI Indexed Summary' : k}</dt>
                  <dd className="review-media-field-value">{v === null || v === undefined ? '—' : String(v)}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReviewMediaPanel;
